import { CardIcon } from '@/components/ui/card';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiDownload, FiExternalLink, FiFileText, FiHeadphones, FiImage } from 'react-icons/fi';
import { PiFile, PiFilePdf } from 'react-icons/pi';
import { RiVimeoFill, RiYoutubeFill } from 'react-icons/ri';
import { Tooltip } from 'react-tooltip';

export default function ChatFileCard({ file, className }) {
  const [videoTitle, setVideoTitle] = useState('');

  const getFileSize = size => {
    if (!size) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let index = 0;
    while (size >= 1024 && index < units.length - 1) {
      size /= 1024;
      index++;
    }
    return `${size.toFixed(2)} ${units[index]}`;
  };

  const getFileIcon = file => {
    const fileType = file?.['attachment-type'] === 'video_link' ? 'video' : file?.['file-type'];
    const youtubeLink = file?.['attachment-type'] === 'video_link' && (file?.url?.includes('youtube.com') || file?.url?.includes('youtu.be'));
    const vimeoLink = file?.['attachment-type'] === 'video_link' && file?.url?.includes('vimeo.com');

    if (youtubeLink) {
      return <RiYoutubeFill className="flex-shrink-0 text-2xl text-neutral" />;
    }

    if (vimeoLink) {
      return <RiVimeoFill className="flex-shrink-0 text-2xl text-neutral" />;
    }

    switch (fileType) {
      // PDF
      case 'pdf':
        return <PiFilePdf className="flex-shrink-0 text-2xl text-neutral" />;

      // Documentos de texto
      case 'docx':
      case 'msword':
      case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'text/plain':
        return <FiFileText className="flex-shrink-0 text-2xl text-neutral" />;

      // Imagens
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return <FiImage className="flex-shrink-0 text-2xl text-neutral" />;

      // Áudio
      case 'mpeg':
      case 'wav':
      case 'ogg':
      case 'mp3':
        return <FiHeadphones className="flex-shrink-0 text-2xl text-neutral" />;

      // Qualquer outro tipo
      default:
        return <PiFile className="flex-shrink-0 text-2xl text-neutral" />;
    }
  };

  const getActionIcon = file => {
    const fileType = file?.['attachment-type'] === 'video_link' ? 'video' : file?.['file-type'];

    switch (fileType) {
      // Vídeos (qualquer formato)
      case 'video':
        return (
          <>
            <FiExternalLink
              data-tooltip-id={String(file?.id)}
              data-tooltip-content="Acessar"
            />
          </>
        );

      // Qualquer outro tipo
      default:
        return (
          <FiDownload
            data-tooltip-id={String(file?.id)}
            data-tooltip-content="Download"
          />
        );
    }
  };

  const handleActionClick = async () => {
    const fileType = file?.['attachment-type'] === 'video_link' ? 'video' : file?.['file-type'];
    const fileUrl = file?.['file-url'] || file?.url;

    if (fileType && fileUrl) {
      if (fileType === 'video') {
        window.open(fileUrl, '_blank');
      } else {
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error('Falha ao baixar o arquivo');

          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = file?.['file-name'] || file?.name || 'download';
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
          toast.error('Erro ao realizar download do arquivo.');
        }
      }
    }
  };

  const getVideoThumbnail = url => {
    // Lógica para extrair o ID do vídeo e construir a URL da thumbnail
    if (url?.includes('vimeo.com')) {
      const videoId = url.split('/').pop();
      return `https://vumbnail.com/${videoId}.jpg`;
    } else if (url?.includes('youtube.com') || url?.includes('youtu.be')) {
      const videoId = url.split('v=')[1] || url.split('/').pop();
      return `https://img.youtube.com/vi/${videoId}/0.jpg`;
    }
    return null;
  };

  // Novo Effect para buscar o título real do vídeo na internet de forma assíncrona
  useEffect(() => {
    const fetchVideoTitle = async () => {
      const fileType = file?.['attachment-type'] === 'video_link' ? 'video' : file?.['file-type'];
      const url = file?.url;

      if (fileType === 'video' && url) {
        try {
          if (url?.includes('youtube.com') || url?.includes('youtu.be')) {
            const res = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(url)}`);
            const data = await res.json();
            if (data.title) setVideoTitle(data.title);
          } else if (url.includes('vimeo.com')) {
            const res = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`);
            const data = await res.json();
            if (data.title) setVideoTitle(data.title);
          }
        } catch (error) {
          console.error('Erro ao buscar título do vídeo:', error);
          setVideoTitle(file?.name || 'Vídeo');
        }
      } else {
        setVideoTitle(file?.name || 'Arquivo');
      }
    };

    fetchVideoTitle();
  }, [file]);

  const isImage = ['jpeg', 'png', 'gif', 'webp'].includes(file?.['file-type']);

  return (
    <div className={`card mt-2 w-full max-w-[350px] p-2 hover:shadow-none ${className || ''}`}>
      {(file?.['attachment-type'] === 'video_link' || isImage) && (
        <img
          src={file?.['attachment-type'] === 'video_link' ? getVideoThumbnail(file?.url) : file?.['thumb-url']}
          alt={file?.['attachment-type'] === 'video_link' ? videoTitle : file?.['file-name']}
          className="h-[90px] w-full rounded object-cover"
        />
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex w-full min-w-0 items-center gap-2">
          {getFileIcon(file)}
          <div className="flex min-w-0 flex-col gap-0 text-sm text-neutral">
            <p
              className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold"
              title={file?.['attachment-type'] === 'video_link' ? videoTitle : file?.['file-name']}
            >
              {file?.['attachment-type'] === 'video_link' ? videoTitle : file?.['file-name']}
            </p>
            {file?.['file-size'] && <span>{getFileSize(file?.['file-size'])}</span>}
          </div>
        </div>
        <CardIcon
          className="flex shrink-0 cursor-pointer items-center justify-center bg-neutral-bright text-2xl font-normal text-neutral"
          onClick={handleActionClick}
        >
          {getActionIcon(file)}
        </CardIcon>
        <Tooltip
          id={String(file?.id)}
          className="tooltip"
        />
      </div>
    </div>
  );
}
